import type { AccessRole } from '@/constants/rbac';
import type {
  BulkConfirmRequest,
  BulkImportError,
  BulkPreviewData,
  BulkPreviewRow,
  BulkUserType,
  EditableBulkRow,
  InternalPreviewData,
  PartnerPreviewData,
} from '../types';
import { validateRowByType } from './validation';

export function mapPreviewRowsToEditable(
  rows: BulkPreviewRow[],
  userType: BulkUserType,
  accessRole?: AccessRole
): EditableBulkRow[] {
  return rows.map((row) => {
    const remoteErrors = row.errors.map((error) => ({
      ...error,
      source: 'remote' as const,
    }));
    const localErrors = validateRowByType(userType, row.data, accessRole);
    const mergedErrors = mergeRowErrors(remoteErrors, localErrors, row.data);

    return {
      id: crypto.randomUUID(),
      rowNumber: row.rowNumber,
      data: row.data,
      status: mergedErrors.length > 0 ? 'invalid' : 'valid',
      errors: mergedErrors,
      removed: false,
      edited: false,
    };
  });
}

export function mergeRowErrors(
  remoteErrors: BulkImportError[],
  localErrors: BulkImportError[],
  data: BulkPreviewData,
  previousData?: BulkPreviewData
): BulkImportError[] {
  const localFields = new Set(localErrors.map((error) => error.field));
  const preservedRemote = remoteErrors.filter((error) => {
    if (localFields.has(error.field)) {
      return false;
    }
    if (previousData) {
      const previousValue = getFieldValue(previousData, error.field);
      const currentValue = getFieldValue(data, error.field);
      if (previousValue.trim() !== currentValue.trim()) {
        return false;
      }
    }
    const currentValue = getFieldValue(data, error.field);
    return currentValue.trim().length > 0;
  });

  return [...localErrors, ...preservedRemote];
}

export function getFieldValue(data: BulkPreviewData, field: string): string {
  const values = data as unknown as Record<string, string | undefined>;
  return String(values[field] ?? '');
}

export function calculateRowSummary(rows: EditableBulkRow[]) {
  const activeRows = rows.filter((row) => !row.removed);
  const validRows = activeRows.filter((row) => row.status === 'valid');
  const invalidRows = activeRows.filter((row) => row.status === 'invalid');
  const removedRows = rows.filter((row) => row.removed);

  return {
    totalRows: rows.length,
    validRows: validRows.length,
    invalidRows: invalidRows.length,
    removedRows: removedRows.length,
    sendableRows: validRows.length,
  };
}

export function buildConfirmPayload(
  userType: BulkUserType,
  partnerIds: string[],
  rows: EditableBulkRow[]
): BulkConfirmRequest {
  const sendableRows = rows.filter((row) => !row.removed && row.status === 'valid');

  if (userType === 'internal') {
    return {
      userType: 'internal',
      partner_ids: partnerIds,
      rows: sendableRows.map((row) => {
        const data = row.data as InternalPreviewData;
        return {
          rowNumber: row.rowNumber,
          nome: data.nome.trim(),
          email: data.email.trim(),
          role: data.role.trim(),
        };
      }),
    };
  }

  return {
    userType: 'partner',
    partner_ids: partnerIds,
    rows: sendableRows.map((row) => {
      const data = row.data as PartnerPreviewData;
      return {
        rowNumber: row.rowNumber,
        nome: data.nome.trim(),
        email: data.email.trim(),
        whatsapp: data.whatsapp.trim(),
      };
    }),
  };
}

export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
